import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Download, Image as ImageIcon, Lock, Eye, Loader, Edit3, RefreshCw, ChevronDown, ChevronUp } from 'lucide-react';
import { PDFDocument } from 'pdf-lib';

// ============================================================
// نافذة التكرار الذكي السريعة
// ============================================================

const API_URL = ''; // السيرفر على نفس الموقع

// أسماء الحقول بالعربية
const FIELD_LABELS = {
    name:         '👤 الاسم',
    passport:     '🛂 رقم الجواز',
    national_id:  '🆔 الرقم القومي',
    age:          '🎂 العمر',
    profession:   '💼 المهنة',
    company:      '🏢 الشركة',
    delegate:     '👔 المندوب',
    phone:        '📞 التليفون',
    governorate:  '📍 المحافظة',
    medicalDate:  '🏥 تاريخ الكشف الطبي',
    visaDate:     '✈️ تاريخ الفيزا',
    travelDate:   '🌍 تاريخ السفر',
};

// ---- رسم الصورة في حقل ----
const drawImageInField = (ctx, img, field) => {
    const fitMode = field.fitMode || 'fill';
    ctx.save();
    if (fitMode === 'fill' || fitMode === 'stretch') {
        ctx.drawImage(img, field.x, field.y, field.width, field.height);
    } else if (fitMode === 'cover') {
        ctx.beginPath();
        ctx.rect(field.x, field.y, field.width, field.height);
        ctx.clip();
        const ir = img.width / img.height, fr = field.width / field.height;
        let dw, dh, ox = 0, oy = 0;
        if (ir > fr) { dh = field.height; dw = dh * ir; ox = (field.width - dw) / 2; }
        else { dw = field.width; dh = dw / ir; oy = (field.height - dh) / 2; }
        ctx.drawImage(img, field.x + ox, field.y + oy, dw, dh);
    } else {
        const ir = img.width / img.height, fr = field.width / field.height;
        let dw, dh, ox = 0, oy = 0;
        if (ir > fr) { dw = field.width; dh = dw / ir; oy = (field.height - dh) / 2; }
        else { dh = field.height; dw = dh * ir; ox = (field.width - dw) / 2; }
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(field.x, field.y, field.width, field.height);
        ctx.drawImage(img, field.x + ox, field.y + oy, dw, dh);
    }
    ctx.restore();
};

// ---- تحميل صورة ----
const loadImg = (src, crossOrigin = false) => new Promise((resolve, reject) => {
    const img = new Image();
    if (crossOrigin) img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
});

// ---- قيمة الحقل من بيانات الشخص ----
const getFieldValue = (type, person, field = {}, personIndex = 0, randomCache = {}) => {
    if (type === 'random_number') {
        const digits = field.randomDigits || 6;
        const key = `${personIndex}_${digits}`;
        if (!randomCache[key]) {
            const min = Math.pow(10, digits - 1);
            const max = Math.pow(10, digits) - 1;
            randomCache[key] = String(Math.floor(Math.random() * (max - min + 1)) + min);
        }
        return randomCache[key];
    }
    if (type === 'static') return field.staticValue || '';
    const map = {
        name:        person.NameArabic       || '',
        passport:    person.PassportNumber   || '',
        national_id: person.NationalID       || '',
        age:         String(person.Age       || ''),
        profession:  person.PassportProfessionName || '',
        company:     person.CompanyName      || '',
        delegate:    person.DelegateName     || '',
        phone:       person.PhoneNumbers     || '',
        governorate: person.Governorate      || '',
        medicalDate: person._manualMedicalDate !== undefined ? person._manualMedicalDate :
                     (person.MedicalExamDate ? new Date(person.MedicalExamDate).toLocaleDateString('ar-EG') : ''),
        visaDate:    person._manualVisaDate    !== undefined ? person._manualVisaDate :
                     (person.VisaDate    ? new Date(person.VisaDate).toLocaleDateString('ar-EG')    : ''),
        travelDate:  person._manualTravelDate  !== undefined ? person._manualTravelDate :
                     (person.TravelDate  ? new Date(person.TravelDate).toLocaleDateString('ar-EG')  : ''),
    };
    return map[type] ?? '';
};

// ---- رسم صفحة كاملة على canvas ----
const renderPageToCanvas = async (pageData, pageFields, person, personIndex, scale, randomCache) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    canvas.width  = pageData.width  * scale;
    canvas.height = pageData.height * scale;
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(pageData.image, 0, 0, canvas.width, canvas.height);

    for (const field of pageFields) {
        const sf = {
            ...field,
            x:        field.x      * scale,
            y:        field.y      * scale,
            width:    field.width  * scale,
            height:   field.height * scale,
            fontSize: (field.fontSize || 18) * scale,
        };
        if (field.type === 'photo') {
            const imagePath = person.PersonalImagePath || person.PassportPhotoUrl || person.PassportImagePath;
            if (imagePath) {
                try {
                    const filename = imagePath.split(/[/\\]/).pop();
                    const img = await loadImg(`${API_URL}/api/smart-repeat/get-image/${encodeURIComponent(filename)}`, true);
                    drawImageInField(ctx, img, sf);
                } catch {
                    ctx.fillStyle = '#f3f4f6'; ctx.fillRect(sf.x, sf.y, sf.width, sf.height);
                }
            } else {
                ctx.fillStyle = '#f3f4f6'; ctx.fillRect(sf.x, sf.y, sf.width, sf.height);
            }
        } else {
            const value = getFieldValue(field.type, person, field, personIndex, randomCache);
            ctx.font = `${field.fontStyle || 'normal'} ${field.fontWeight || 'normal'} ${sf.fontSize}px ${field.fontFamily || 'Arial'}`;
            ctx.fillStyle = field.textColor || '#000000';
            ctx.textBaseline = 'middle';
            const align = field.textAlign || 'right';
            ctx.textAlign = align;
            const x = align === 'right' ? sf.x + sf.width : align === 'center' ? sf.x + sf.width / 2 : sf.x;
            ctx.fillText(value || '-', x, sf.y + sf.height / 2);
        }
    }
    return canvas;
};

// ---- اسم ملف PDF ----
const buildFileName = (person, ext = 'pdf') => {
    const name = (person.NameArabic || '').replace(/\s+/g, '_').replace(/[^\u0600-\u06FFa-zA-Z0-9_]/g, '');
    const num  = person.PassportNumber || '';
    return `${name || 'person'}_${num || '1'}.${ext}`;
};

// ============================================================
// المكوّن الرئيسي
// ============================================================
const SmartRepeatQuickModal = ({ isOpen, onClose, passportData }) => {

    const [templates,           setTemplates]           = useState([]);
    const [isLoadingTemplates,  setIsLoadingTemplates]  = useState(false);
    const [selectedTemplate,    setSelectedTemplate]    = useState(null);
    const [templateData,        setTemplateData]        = useState(null);
    const [isLoadingTemplate,   setIsLoadingTemplate]   = useState(false);
    const [showPasswordInput,   setShowPasswordInput]   = useState(false);
    const [passwordValue,       setPasswordValue]       = useState('');
    const [passwordError,       setPasswordError]       = useState('');
    const [pendingTemplate,     setPendingTemplate]     = useState(null);
    const [showManualEdit,      setShowManualEdit]      = useState(false);
    const [manualData,          setManualData]          = useState({});
    const [useManualData,       setUseManualData]       = useState(false);
    const [previewCanvas,       setPreviewCanvas]       = useState(null);
    const [isGeneratingPreview, setIsGeneratingPreview] = useState(false);
    const [isExporting,         setIsExporting]         = useState(false);
    const [exportProgress,      setExportProgress]      = useState(0);
    const [exportStatus,        setExportStatus]        = useState('');

    const randomCacheRef  = useRef({});
    const previewTimerRef = useRef(null);

    // إعادة الضبط عند الفتح
    useEffect(() => {
        if (isOpen) {
            loadTemplates();
            setSelectedTemplate(null);
            setTemplateData(null);
            setShowPasswordInput(false);
            setPasswordValue('');
            setPasswordError('');
            setPreviewCanvas(null);
            setShowManualEdit(false);
            setManualData({});
            setUseManualData(false);
            randomCacheRef.current = {};
        }
    }, [isOpen]);

    // توليد معاينة عند تغيير القالب أو البيانات
    useEffect(() => {
        if (templateData) {
            if (previewTimerRef.current) clearTimeout(previewTimerRef.current);
            previewTimerRef.current = setTimeout(() => generatePreview(), 300);
        }
    }, [templateData, manualData, useManualData]);

    // بناء كائن الشخص (أصلي أو يدوي)
    const buildPersonData = useCallback(() => {
        const base = passportData?.[0] || {};
        if (!useManualData) return base;
        return {
            ...base,
            NameArabic:             manualData.name        !== undefined ? manualData.name        : base.NameArabic,
            PassportNumber:         manualData.passport    !== undefined ? manualData.passport    : base.PassportNumber,
            NationalID:             manualData.national_id !== undefined ? manualData.national_id : base.NationalID,
            Age:                    manualData.age         !== undefined ? manualData.age         : base.Age,
            PassportProfessionName: manualData.profession  !== undefined ? manualData.profession  : base.PassportProfessionName,
            CompanyName:            manualData.company     !== undefined ? manualData.company     : base.CompanyName,
            DelegateName:           manualData.delegate    !== undefined ? manualData.delegate    : base.DelegateName,
            PhoneNumbers:           manualData.phone       !== undefined ? manualData.phone       : base.PhoneNumbers,
            Governorate:            manualData.governorate !== undefined ? manualData.governorate : base.Governorate,
            _manualMedicalDate:     manualData.medicalDate,
            _manualVisaDate:        manualData.visaDate,
            _manualTravelDate:      manualData.travelDate,
        };
    }, [passportData, manualData, useManualData]);

    // توليد المعاينة
    const generatePreview = async () => {
        if (!templateData?.loadedPages?.length) return;
        setIsGeneratingPreview(true);
        try {
            const pageData   = templateData.loadedPages[0];
            const pageFields = templateData.normalizedFields[0] || [];
            const person     = buildPersonData();
            const canvas     = await renderPageToCanvas(pageData, pageFields, person, 0, 1.5, {});
            setPreviewCanvas(canvas.toDataURL('image/png'));
        } catch (err) { console.error('Preview error:', err); }
        finally { setIsGeneratingPreview(false); }
    };

    const loadTemplates = async () => {
        setIsLoadingTemplates(true);
        try {
            const res  = await fetch(`${API_URL}/api/smart-repeat/templates`);
            const data = await res.json();
            if (data.success) setTemplates(data.templates);
        } catch (err) { console.error(err); }
        finally { setIsLoadingTemplates(false); }
    };

    const handleSelectTemplate = async (template, password = null) => {
        setIsLoadingTemplate(true);
        setPasswordError('');
        try {
            const res  = await fetch(`${API_URL}/api/smart-repeat/templates/${template.id}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ password }),
            });
            const data = await res.json();

            if (data.requirePassword) { setPendingTemplate(template); setShowPasswordInput(true); setIsLoadingTemplate(false); return; }
            if (data.wrongPassword)   { setPasswordError('كلمة المرور غير صحيحة ❌'); setIsLoadingTemplate(false); return; }

            if (data.success) {
                const tpl = data.template;
                let loadedPages = [];
                if (tpl.pages?.length > 0) {
                    for (const pg of tpl.pages) {
                        const img = await loadImg(pg.src);
                        loadedPages.push({ image: img, src: pg.src, width: pg.width, height: pg.height });
                    }
                } else if (tpl.templateImage) {
                    const img = await loadImg(tpl.templateImage);
                    loadedPages = [{ image: img, src: tpl.templateImage, width: img.width, height: img.height }];
                }

                let normalizedFields = {};
                if (tpl.fields) {
                    if (Array.isArray(tpl.fields)) {
                        normalizedFields = { 0: tpl.fields };
                    } else {
                        Object.keys(tpl.fields).forEach(k => {
                            normalizedFields[k] = Array.isArray(tpl.fields[k]) ? tpl.fields[k] : [];
                        });
                    }
                }

                // الحقول القابلة للتعديل (فريدة، بدون photo/static/random)
                const allFields = Object.values(normalizedFields).flat();
                const editableTypes = [...new Set(
                    allFields
                        .filter(f => !['photo', 'static', 'random_number'].includes(f.type))
                        .map(f => f.type)
                )];

                // تهيئة البيانات اليدوية بالقيم الحالية
                const person0 = passportData?.[0] || {};
                const initManual = {};
                editableTypes.forEach(type => { initManual[type] = getFieldValue(type, person0, {}, 0, {}); });

                setManualData(initManual);
                setTemplateData({ ...tpl, loadedPages, normalizedFields, editableTypes });
                setSelectedTemplate(template);
                setShowPasswordInput(false);
                setPasswordValue('');
                setUseManualData(false);
                setShowManualEdit(false);
                randomCacheRef.current = {};
            }
        } catch (err) { console.error(err); }
        finally { setIsLoadingTemplate(false); }
    };

    const handlePasswordSubmit = () => {
        if (!passwordValue.trim()) { setPasswordError('يرجى إدخال كلمة المرور'); return; }
        handleSelectTemplate(pendingTemplate, passwordValue);
    };

    const handleManualChange = (type, value) => {
        setManualData(prev => ({ ...prev, [type]: value }));
        if (!useManualData) setUseManualData(true);
    };

    const resetManualData = () => {
        const person0 = passportData?.[0] || {};
        const reset = {};
        (templateData?.editableTypes || []).forEach(type => { reset[type] = getFieldValue(type, person0, {}, 0, {}); });
        setManualData(reset);
        setUseManualData(false);
    };

    // ---- تنزيل PDF ----
    const handleDownloadPDF = async () => {
        if (!templateData) return;
        setIsExporting(true); setExportProgress(0); setExportStatus('جاري إنشاء PDF...');
        try {
            const person = buildPersonData();
            const cache  = {};
            const pdfDoc = await PDFDocument.create();
            for (let i = 0; i < templateData.loadedPages.length; i++) {
                const pageData   = templateData.loadedPages[i];
                const pageFields = templateData.normalizedFields[i] || [];
                const canvas     = await renderPageToCanvas(pageData, pageFields, person, 0, 3, cache);
                const pngBytes   = await fetch(canvas.toDataURL('image/png', 1.0)).then(r => r.arrayBuffer());
                const pngImage   = await pdfDoc.embedPng(pngBytes);
                const pdfPage    = pdfDoc.addPage([pageData.width, pageData.height]);
                pdfPage.drawImage(pngImage, { x: 0, y: 0, width: pageData.width, height: pageData.height });
                setExportProgress(Math.round(((i + 1) / templateData.loadedPages.length) * 85));
            }
            setExportStatus('جاري حفظ الملف...'); setExportProgress(92);
            const pdfBytes = await pdfDoc.save();
            const fileName = buildFileName(person, 'pdf');
            const a = document.createElement('a');
            a.href = URL.createObjectURL(new Blob([pdfBytes], { type: 'application/pdf' }));
            a.download = fileName;
            document.body.appendChild(a); a.click(); document.body.removeChild(a);
            URL.revokeObjectURL(a.href);
            setExportProgress(100); setExportStatus(`✅ تم: ${fileName}`);
        } catch (err) { setExportStatus('❌ فشل: ' + err.message); }
        finally { setTimeout(() => { setIsExporting(false); setExportProgress(0); setExportStatus(''); }, 2500); }
    };

    // ---- تنزيل صورة ----
    const handleDownloadImage = async () => {
        if (!templateData) return;
        setIsExporting(true); setExportProgress(0); setExportStatus('جاري إنشاء الصورة...');
        try {
            const person = buildPersonData();
            const cache  = {};
            if (templateData.loadedPages.length === 1) {
                const canvas = await renderPageToCanvas(templateData.loadedPages[0], templateData.normalizedFields[0] || [], person, 0, 3, cache);
                setExportProgress(80);
                const blob = await new Promise(r => canvas.toBlob(r, 'image/png', 1.0));
                const fileName = buildFileName(person, 'png');
                const a = document.createElement('a');
                a.href = URL.createObjectURL(blob); a.download = fileName;
                document.body.appendChild(a); a.click(); document.body.removeChild(a);
                URL.revokeObjectURL(a.href);
                setExportProgress(100); setExportStatus(`✅ تم: ${fileName}`);
            } else {
                const JSZip = (await import('jszip')).default;
                const zip   = new JSZip();
                const base  = buildFileName(person, '').replace('.', '');
                for (let i = 0; i < templateData.loadedPages.length; i++) {
                    const canvas = await renderPageToCanvas(templateData.loadedPages[i], templateData.normalizedFields[i] || [], person, 0, 3, cache);
                    const blob   = await new Promise(r => canvas.toBlob(r, 'image/png', 1.0));
                    zip.file(`${base}_p${i + 1}.png`, blob);
                    setExportProgress(Math.round(((i + 1) / templateData.loadedPages.length) * 80));
                }
                setExportStatus('ضغط الملفات...'); setExportProgress(90);
                const content = await zip.generateAsync({ type: 'blob' });
                const a = document.createElement('a');
                a.href = URL.createObjectURL(content); a.download = `${base}_images.zip`;
                document.body.appendChild(a); a.click(); document.body.removeChild(a);
                URL.revokeObjectURL(a.href);
                setExportProgress(100); setExportStatus('✅ تم تنزيل الصور');
            }
        } catch (err) { setExportStatus('❌ فشل التصدير'); }
        finally { setTimeout(() => { setIsExporting(false); setExportProgress(0); setExportStatus(''); }, 2500); }
    };

    if (!isOpen) return null;

    const personName    = passportData?.[0]?.NameArabic || '';
    const editableTypes = templateData?.editableTypes || [];
    const hasManualChanges = useManualData;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-70 z-[500] flex items-center justify-center p-3" dir="rtl">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[92vh] overflow-hidden flex flex-col">

                {/* Header */}
                <div className="bg-gradient-to-r from-orange-500 to-amber-500 px-6 py-4 flex items-center justify-between flex-shrink-0">
                    <div>
                        <h2 className="text-white font-bold text-xl">🔁 تكرار ذكي سريع</h2>
                        {personName && <p className="text-orange-100 text-sm mt-0.5">{personName}</p>}
                    </div>
                    <button onClick={onClose} className="text-white hover:bg-white/20 rounded-full p-2 transition">
                        <X size={22} />
                    </button>
                </div>

                {/* Body */}
                <div className="flex flex-1 overflow-hidden min-h-0">

                    {/* قائمة القوالب */}
                    <div className="w-52 border-l border-gray-200 bg-gray-50 overflow-y-auto flex-shrink-0 flex flex-col">
                        <div className="px-3 py-2.5 border-b border-gray-200">
                            <h3 className="font-bold text-gray-500 text-xs uppercase tracking-wide">القوالب</h3>
                        </div>
                        {isLoadingTemplates ? (
                            <div className="flex items-center justify-center p-6"><Loader size={20} className="text-orange-400 animate-spin" /></div>
                        ) : templates.length === 0 ? (
                            <div className="p-4 text-center text-gray-400 text-sm">لا توجد قوالب</div>
                        ) : (
                            <div className="p-2 space-y-1">
                                {templates.map(t => (
                                    <button
                                        key={t.id}
                                        onClick={() => handleSelectTemplate(t)}
                                        disabled={isLoadingTemplate}
                                        className={`w-full text-right px-3 py-2 rounded-lg text-sm font-medium transition flex items-center gap-2 ${
                                            selectedTemplate?.id === t.id
                                                ? 'bg-orange-500 text-white shadow'
                                                : 'bg-white text-gray-700 hover:bg-orange-50 border border-gray-200'
                                        }`}
                                    >
                                        {t.isProtected
                                            ? <Lock size={12} className={selectedTemplate?.id === t.id ? 'text-orange-100' : 'text-orange-400'} />
                                            : <span>📄</span>}
                                        <span className="truncate flex-1">{t.name}</span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* المحتوى الرئيسي */}
                    <div className="flex-1 overflow-y-auto min-h-0">

                        {/* باسورد */}
                        {showPasswordInput && !isLoadingTemplate && (
                            <div className="p-5">
                                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                                    <div className="flex items-center gap-2 mb-3">
                                        <Lock size={18} className="text-amber-600" />
                                        <h4 className="font-bold text-amber-800 text-sm">هذا القالب محمي بكلمة مرور</h4>
                                    </div>
                                    <input
                                        type="password" value={passwordValue} autoFocus
                                        onChange={e => { setPasswordValue(e.target.value); setPasswordError(''); }}
                                        onKeyDown={e => e.key === 'Enter' && handlePasswordSubmit()}
                                        placeholder="أدخل كلمة المرور..."
                                        className={`w-full border rounded-lg px-3 py-2 text-sm mb-2 focus:outline-none focus:ring-2 focus:ring-amber-400 ${passwordError ? 'border-red-400 bg-red-50' : 'border-gray-300'}`}
                                    />
                                    {passwordError && <p className="text-red-600 text-xs mb-2">{passwordError}</p>}
                                    <div className="flex gap-2">
                                        <button onClick={handlePasswordSubmit} className="flex-1 bg-amber-500 hover:bg-amber-600 text-white font-bold py-2 rounded-lg text-sm">✅ دخول</button>
                                        <button onClick={() => { setShowPasswordInput(false); setPasswordValue(''); setPasswordError(''); }} className="px-3 py-2 border border-gray-300 rounded-lg text-gray-600 text-sm">إلغاء</button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* تحميل */}
                        {isLoadingTemplate && (
                            <div className="flex-1 flex items-center justify-center p-12">
                                <div className="text-center">
                                    <Loader size={32} className="text-orange-500 animate-spin mx-auto mb-2" />
                                    <p className="text-gray-500 text-sm">جاري تحميل القالب...</p>
                                </div>
                            </div>
                        )}

                        {/* اختر قالب */}
                        {!selectedTemplate && !showPasswordInput && !isLoadingTemplate && (
                            <div className="flex items-center justify-center p-12">
                                <div className="text-center text-gray-400">
                                    <div className="text-5xl mb-3">📄</div>
                                    <p className="font-medium">اختر قالباً من القائمة</p>
                                    <p className="text-sm mt-1">سيتم تطبيق بيانات الجواز على القالب</p>
                                </div>
                            </div>
                        )}

                        {/* القالب محمّل */}
                        {templateData && selectedTemplate && !showPasswordInput && !isLoadingTemplate && (
                            <div className="p-4 space-y-3">

                                {/* اسم القالب */}
                                <div className="flex items-center gap-2 bg-orange-50 border border-orange-200 rounded-xl px-3 py-2.5">
                                    <span>📂</span>
                                    <div className="flex-1 min-w-0">
                                        <p className="font-bold text-orange-800 text-sm truncate">{selectedTemplate.name}</p>
                                        <p className="text-xs text-orange-500">
                                            {templateData.loadedPages?.length > 1 ? `${templateData.loadedPages.length} صفحات` : 'صفحة واحدة'}
                                            {' · '}{editableTypes.length} حقل قابل للتعديل
                                        </p>
                                    </div>
                                    {hasManualChanges && (
                                        <span className="bg-blue-100 text-blue-700 text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap">بيانات يدوية</span>
                                    )}
                                </div>

                                {/* معاينة */}
                                <div className="rounded-xl overflow-hidden border border-gray-200 shadow-sm">
                                    <div className="bg-gray-100 px-3 py-1.5 text-xs text-gray-500 flex items-center justify-between border-b border-gray-200">
                                        <div className="flex items-center gap-1">
                                            <Eye size={11} />
                                            <span>معاينة مباشرة</span>
                                            {hasManualChanges && <span className="text-blue-600 font-medium">• بيانات يدوية</span>}
                                        </div>
                                        {isGeneratingPreview && <Loader size={11} className="animate-spin text-orange-400" />}
                                    </div>
                                    {previewCanvas
                                        ? <img src={previewCanvas} alt="معاينة" className="w-full object-contain max-h-60" />
                                        : <div className="h-32 flex items-center justify-center bg-gray-50"><Loader size={20} className="text-gray-300 animate-spin" /></div>
                                    }
                                </div>

                                {/* ─── تعديل يدوي ─── */}
                                {editableTypes.length > 0 && (
                                    <div className="border border-gray-200 rounded-xl overflow-hidden">
                                        <button
                                            onClick={() => setShowManualEdit(v => !v)}
                                            className="w-full flex items-center justify-between px-4 py-3 bg-gradient-to-r from-blue-50 to-indigo-50 hover:from-blue-100 hover:to-indigo-100 transition text-right"
                                        >
                                            <div className="flex items-center gap-2">
                                                <Edit3 size={15} className="text-blue-600" />
                                                <span className="font-bold text-blue-800 text-sm">✏️ تعديل البيانات يدوياً</span>
                                                {hasManualChanges && (
                                                    <span className="bg-blue-600 text-white text-xs px-2 py-0.5 rounded-full">مُعدَّل</span>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-2">
                                                {hasManualChanges && (
                                                    <button
                                                        onClick={e => { e.stopPropagation(); resetManualData(); }}
                                                        className="flex items-center gap-1 text-xs text-gray-500 hover:text-red-600 bg-white border border-gray-300 hover:border-red-300 rounded-lg px-2 py-1 transition"
                                                    >
                                                        <RefreshCw size={11} /> إعادة تعيين
                                                    </button>
                                                )}
                                                {showManualEdit ? <ChevronUp size={16} className="text-blue-600" /> : <ChevronDown size={16} className="text-blue-600" />}
                                            </div>
                                        </button>

                                        {showManualEdit && (
                                            <div className="p-3 bg-white">
                                                <p className="text-xs text-gray-500 bg-blue-50 rounded-lg px-3 py-2 mb-3">
                                                    💡 عدّل أي حقل وستظهر التغييرات فوراً في المعاينة — هذه البيانات ستُستخدم عند التنزيل
                                                </p>
                                                <div className="grid grid-cols-2 gap-2">
                                                    {editableTypes.map(type => (
                                                        <div key={type} className="flex flex-col gap-1">
                                                            <label className="text-xs font-semibold text-gray-600">
                                                                {FIELD_LABELS[type] || type}
                                                            </label>
                                                            <input
                                                                type="text"
                                                                value={manualData[type] ?? ''}
                                                                onChange={e => handleManualChange(type, e.target.value)}
                                                                placeholder={`...`}
                                                                dir="rtl"
                                                                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition"
                                                            />
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* شريط التقدم */}
                                {isExporting && (
                                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
                                        <p className="text-sm font-medium text-blue-700 mb-2 flex items-center gap-2">
                                            <Loader size={13} className="animate-spin" />{exportStatus}
                                        </p>
                                        <div className="w-full bg-blue-200 rounded-full h-1.5">
                                            <div className="bg-blue-500 h-1.5 rounded-full transition-all duration-300" style={{ width: `${exportProgress}%` }} />
                                        </div>
                                    </div>
                                )}

                                {/* أزرار التنزيل */}
                                {!isExporting && (
                                    <div className="grid grid-cols-2 gap-3">
                                        <button
                                            onClick={handleDownloadPDF}
                                            className="flex items-center justify-center gap-3 bg-gradient-to-r from-red-500 to-rose-600 hover:from-red-600 hover:to-rose-700 text-white rounded-xl py-3.5 px-4 transition shadow hover:shadow-md"
                                        >
                                            <Download size={20} />
                                            <div className="text-right">
                                                <div className="text-sm font-bold">تنزيل PDF</div>
                                                <div className="text-xs opacity-80">ملف جاهز للحفظ</div>
                                            </div>
                                        </button>
                                        <button
                                            onClick={handleDownloadImage}
                                            className="flex items-center justify-center gap-3 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white rounded-xl py-3.5 px-4 transition shadow hover:shadow-md"
                                        >
                                            <ImageIcon size={20} />
                                            <div className="text-right">
                                                <div className="text-sm font-bold">تنزيل صورة</div>
                                                <div className="text-xs opacity-80">PNG عالي الجودة</div>
                                            </div>
                                        </button>
                                    </div>
                                )}

                            </div>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="border-t border-gray-200 px-5 py-2.5 bg-gray-50 flex justify-end flex-shrink-0">
                    <button onClick={onClose} className="px-5 py-2 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-100 transition text-sm font-medium">
                        إغلاق
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SmartRepeatQuickModal;